import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {
    NewEditProjectModalComponent,
    NewEditProjectModalConfig,
    NewEditProjectModalResult
} from '../../modals/new-edit-project-modal/new-edit-project-modal.component';
import {Component} from '@angular/core';
import {ETableColDataType, ETableColFilterType} from '../../../../sdk/src/components/generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {CpmCProjectApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-project.api-service';
import {CpmCProjectApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-project.api-model';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

/**
 * Project CRUD table component
 */
@Component({
    selector: 'app-project-crud',
    templateUrl: './project-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class ProjectCRUDComponent extends IBaseCRUDComponent<
    CpmCProjectApiModel,
    NewEditProjectModalComponent,
    NewEditProjectModalConfig,
    NewEditProjectModalResult
> {
    protected helpVisible = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'ProjectApiModel',
            getModelHumanIdentifier: (model: CpmCProjectApiModel) => model.name ?? '<Unknown project>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCProjectApiModel) =>
                CpmCProjectApiService.deleteProject(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditProjectModalComponent,
            getDialogServiceParameters: NewEditProjectModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCProjectApiModel) => new NewEditProjectModalConfig(model),

            // Authorization config
            authorizationNew: CpmCProjectApiService.endpointNew(this.FAKE_UUID),
            authorizationEdit: CpmCProjectApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCProjectApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading projects...';

        const result = await firstValueFrom(CpmCProjectApiService.getProjectList({}, this.paginatorConfig));

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'description',
                header: 'Description',
                dataType: ETableColDataType.LONG_TEXT,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            }
        ];

        this.addCreatedColumns();
        this.addCommunicationButtons();

        this.addAccessButtonColumn((row) => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([CustomerUrlBuilderUtil.customerProject(customerUUID!, row.uuid)]);
        });
    }
}
